import React, { useEffect, useRef, useState } from 'react';
import {
  BodyText,
  Button,
  Checkbox,
  Div,
  Flex,
  GridBag,
  Gutter,
  Icon,
  InlineNotification,
  Input,
  ModalDialog,
  SearchInputWithScope,
  Table,
  Title,
} from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  AbstractProductOverview,
  AdminCategory,
  LocaleResponse,
  SalesChannel,
  VariantOverview,
} from '@cdaas/api-clients/dist/product-service-openapi';

import EditableItem from '../../../../shared/EditableItem/EditableItem';
import ArrowButton from '../../../../shared/ListUtil/ArrowButton';
import { AdminBundleItemWithKey } from '../../../../utils/types';
import { updateEntityByIndex } from '../../../../utils/entities';
import { createAdminBundleItem } from '../../../../utils/bundles';
import { getMainSiteNavigationComponentHeight } from '../../../App/Header/Header';
import { ensureEditableItemInputChangesSavedOnSaveBtnClick, Errors, VariantEverything } from '../utils';
import {
  formulateBundleMainItemOrAddonsSummaryErrorMessage,
  formulateSingleVariantErrorMessage,
  VariantEverythingWithAutomaticPricingErrors,
} from './utils';
import { productServiceAdminProductsClient, productServiceAdminProductVariantsClient } from '../../../../api-client/product-service-client';

import './Addons.scss';

interface AddonsProps {
  items: AdminBundleItemWithKey[] | undefined;
  handleItemsChange: (items: AdminBundleItemWithKey[]) => void;
  variants: VariantEverythingWithAutomaticPricingErrors[];
  handleVariantsChange: (variants: (VariantOverview | VariantEverything)[]) => Promise<boolean>;
  allCategoriesOfSystem: AdminCategory[];
  currentLocale: LocaleResponse;
  locales: LocaleResponse[];
  erroneousSalesChannels: SalesChannel[];
  errors: Errors['addonProductVariants'];
}

const saveProductGroupButtonId = 'save-product-group-btn';

function Addons(props: AddonsProps) {
  const { items, handleItemsChange, variants, handleVariantsChange, erroneousSalesChannels, allCategoriesOfSystem, locales, currentLocale, errors } =
    props;
  const { t } = useTranslation('bundlescreen');

  const history = useHistory();

  const addonsTableRef = useRef<HTMLTableElement>(null);
  const addOrUpdateAddonFormRef = useRef<HTMLDivElement>(null);
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const scrollToAddonsTable = () => {
    if (addonsTableRef.current !== null) {
      const pos = addonsTableRef.current.offsetTop - getMainSiteNavigationComponentHeight();
      window.scrollTo({ top: pos, behavior: 'smooth' });
    }
  };
  const scrollToAddOrUpdateAddonForm = () => {
    if (addOrUpdateAddonFormRef.current !== null) {
      const pos = addOrUpdateAddonFormRef.current.offsetTop - getMainSiteNavigationComponentHeight();
      window.scrollTo({ top: pos, behavior: 'smooth' });
    }
  };

  // 3 cases:
  // 1. Adding a new product group -> both setting product group name and adding product variants to product group should be open
  // 2. Adding products to existing product group -> only adding product variants to product group should be open
  // 3. Updating name of existing product group -> only setting product group name should be open
  const [setProductGroupNameOpen, setSetProductGroupNameOpen] = useState<boolean>(false);
  const [addProductsToProductGroupOpen, setAddProductsToProductGroupOpen] = useState<boolean>(false);

  const [searchCategory, setSearchCategory] = useState<AdminCategory>(allCategoriesOfSystem[0]);
  const [productSearchInput, setProductSearchInput] = useState<string>('');
  const [currentProductOptions, setCurrentProductOptions] = useState<AbstractProductOverview[]>([]);
  const [currentProductVariantOptions, setCurrentProductVariantOptions] = useState<(VariantOverview & { duplicate: boolean })[]>([]);
  const [variantsSearchInput, setVariantsSearchInput] = useState<string>('');
  const [showingVariantOptions, setShowingVariantOptions] = useState<boolean>(false);
  const [searchForProductsError, setSearchForProductsError] = useState<boolean>(false);
  const [fetchProductError, setFetchProductError] = useState<boolean>(false);

  const [productGroupsTableOpenStates, setProductGroupsTableOpenStates] = useState(
    (items || []).map(item => ({ itemIdOrKey: item.id || item.key, open: false })),
  );
  const [removeVariantsConfirmationModalOpen, setRemoveVariantsConfirmationModalOpen] = useState<boolean>(false);

  // Product variants that are already part of some addon of bundle -> used for batch deleting
  const [selectedVariants, setSelectedVariants] = useState<number[]>([]);
  // Product variants that are possibly added to some addon of bundle
  const [selectedNewVariants, setSelectedNewVariants] = useState<VariantOverview[]>([]);

  // In case existing product group is updated, store it's id or key here
  const [currentProductGroupIdOrKey, setCurrentProductGroupIdOrKey] = useState<number | string>();
  // Store the required info under update until user actually clicks to save the changes
  const [currentProductGroupName, setCurrentProductGroupName] = useState<string>('');
  const [currentProductGroupNameTranslations, setCurrentProductGroupNameTranslations] = useState<{ name: string; localeId: number }[]>([]);
  const [productGroupNameReferenceInputOpen, setProductGroupNameReferenceInputOpen] = useState<boolean>(false);

  const currentProductGroupNameTranslation = currentProductGroupNameTranslations?.find(translation => translation.localeId === currentLocale.id);

  const [saveProductGroupChanges, setSaveProductGroupChanges] = useState<boolean>(false);

  useEffect(() => {
    if (saveProductGroupChanges) {
      handleAddOrUpdateProductGroup();
      setSaveProductGroupChanges(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveProductGroupChanges]);

  useEffect(() => {
    if (initialLoad) {
      // Don't scroll when bundle page is opened
      setInitialLoad(false);
    } else if (setProductGroupNameOpen || addProductsToProductGroupOpen) {
      scrollToAddOrUpdateAddonForm();
    } else {
      scrollToAddonsTable();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setProductGroupNameOpen, addProductsToProductGroupOpen]);

  const handleChangeProductGroupNameTranslation = (nameTranslation: string) => {
    const index = (currentProductGroupNameTranslations || []).findIndex(translation => translation.localeId === currentLocale.id);
    if (index !== -1 && currentProductGroupNameTranslations) {
      const updatedTranslation = { ...currentProductGroupNameTranslations[index], name: nameTranslation };
      const updatedTranslations = updateEntityByIndex(currentProductGroupNameTranslations, index, updatedTranslation);
      setCurrentProductGroupNameTranslations(updatedTranslations);
    }
  };

  const checkProductGroupTableOpenState = (idOrKey: number | string | undefined) => {
    const index = (productGroupsTableOpenStates || []).findIndex(pgroup => pgroup.itemIdOrKey?.toString() === idOrKey?.toString());
    return index !== -1 && productGroupsTableOpenStates[index].open;
  };

  const toggleProductGroupTableOpenState = (idOrKey: number | string | undefined) => {
    const index = (productGroupsTableOpenStates || []).findIndex(pgroup => pgroup.itemIdOrKey?.toString() === idOrKey?.toString());
    if (index !== -1) {
      const existingProductGroupOpenState = productGroupsTableOpenStates[index];
      const updatedProductGroupOpenState = { itemIdOrKey: existingProductGroupOpenState.itemIdOrKey, open: !existingProductGroupOpenState.open };
      const updatedProductGroupsTableOpenState = updateEntityByIndex(productGroupsTableOpenStates, index, updatedProductGroupOpenState);
      setProductGroupsTableOpenStates(updatedProductGroupsTableOpenState);
    }
  };

  const handleSearchCategoryChange = (searchCategoryId: number) => {
    const newSearchCategory = allCategoriesOfSystem.find(category => category.id === searchCategoryId);
    if (newSearchCategory) setSearchCategory(newSearchCategory);
  };

  function isDefined<T>(argument: T | undefined): argument is T {
    return argument !== undefined;
  }

  const getAllVariantsOfAddons = () => {
    const allVariantsOfAddons: VariantOverview[] = [];
    items?.forEach(item => {
      const variantsOfItem = item.options.map(variantId => variants?.find(variant => variant.id === variantId)).filter(isDefined);
      allVariantsOfAddons.push(...variantsOfItem);
    });
    return allVariantsOfAddons;
  };

  const checkAllVariantsSelected = () => {
    return getAllVariantsOfAddons().length === selectedVariants.length;
  };

  const checkVariantsOfItemSelected = (item: AdminBundleItemWithKey) => {
    return item.options.every(option => selectedVariants.includes(option));
  };

  const checkVariantSelected = (variantId: number) => {
    return selectedVariants.findIndex(selectedVariantId => selectedVariantId === variantId) !== -1;
  };

  const handleSelectAllVariants = () => {
    setSelectedVariants(getAllVariantsOfAddons().map(variant => variant.id) || []);
  };

  const handleDeselectAllVariants = () => {
    setSelectedVariants([]);
  };

  const handleSelectVariantsOfItem = (item: AdminBundleItemWithKey) => {
    setSelectedVariants(Array.from(new Set([...selectedVariants, ...item.options])));
  };

  const handleDeselectVariantsOfItem = (item: AdminBundleItemWithKey) => {
    const updatedSelectedVariants = selectedVariants.filter(selectedVariantId => !item.options.includes(selectedVariantId));
    setSelectedVariants(updatedSelectedVariants);
  };

  const handleSelectVariant = (variantId: number) => {
    setSelectedVariants([...selectedVariants, variantId]);
  };

  const handleDeselectVariant = (variantId: number) => {
    const updatedSelectedVariants = selectedVariants.filter(selectedVariantId => selectedVariantId !== variantId);
    setSelectedVariants(updatedSelectedVariants);
  };

  const handleSelectedVariantsRemove = async () => {
    // Remove all selected variants. If all variants of some item are removed -> delete the whole item
    const updatedItems = (items || [])
      .map(item => {
        const filteredVariants = (item.options || []).filter(variantId => !selectedVariants.includes(variantId));
        return { ...item, options: filteredVariants };
      })
      .filter(item => {
        return item.options.length > 0;
      });

    const updatedVariants = variants.filter(variant => !selectedVariants.includes(variant.id));
    const success = await handleVariantsChange(updatedVariants);

    if (success) {
      handleItemsChange(updatedItems);
      handleDeselectAllVariants();
      setRemoveVariantsConfirmationModalOpen(false);
    }
  };

  const handleRemoveVariantsBtnClick = () => {
    // If all variants of some item are about to be removed -> show confirmation dialog to user
    for (const item of items || []) {
      if (item.options.every(option => selectedVariants.includes(option))) {
        setRemoveVariantsConfirmationModalOpen(true);
        return;
      }
    }
    // If all variants of none of the items are about to be removed -> remove without confirmation
    handleSelectedVariantsRemove();
  };

  const handleStartAddingProductGroup = () => {
    setCurrentProductOptions([]);
    setCurrentProductGroupNameTranslations(locales.map(locale => ({ name: '', localeId: locale.id })));
    setSetProductGroupNameOpen(true);
    setAddProductsToProductGroupOpen(true);
  };

  const handleStartUpdatingExistingProductGroupProducts = (item: AdminBundleItemWithKey) => {
    setCurrentProductOptions([]);
    // Possibility to call this is hidden in UI when either addProductsToProductGroupOpen or setProductGroupNameOpen is true already
    setCurrentProductGroupIdOrKey(item.id || item.key);
    setAddProductsToProductGroupOpen(true);
  };

  const handleCancelAddOrUpdateProductGroup = () => {
    setSetProductGroupNameOpen(false);
    setAddProductsToProductGroupOpen(false);
    setShowingVariantOptions(false);
    setCurrentProductVariantOptions([]);
    setSelectedNewVariants([]);
    setProductSearchInput('');
    setVariantsSearchInput('');
    setCurrentProductGroupName('');
    setCurrentProductGroupNameTranslations([]);
    setCurrentProductGroupIdOrKey(undefined);
  };

  const handleStartUpdatingExistingProductGroupName = (item: AdminBundleItemWithKey) => {
    // Possibility to call this is hidden in UI when either addProductsToProductGroupOpen or setProductGroupNameOpen is true already
    setCurrentProductGroupName(item.name);
    const currentLocales = locales.map(locale => {
      const existingLocale = (item.translations || []).find(translation => translation.locale_id === locale.id);
      if (existingLocale) return { name: existingLocale.name || '', localeId: locale.id };
      else return { name: '', localeId: locale.id };
    });
    setCurrentProductGroupNameTranslations(currentLocales);
    setCurrentProductGroupIdOrKey(item.id || item.key);
    setSetProductGroupNameOpen(true);
  };

  const handleAddOrUpdateProductGroup = async () => {
    // Make sure same variant is not added multiple times to same bundle
    const getFilteredVariants = (newVariants: VariantOverview[]) => {
      const allVariantIdsOfBundle = variants.map(variant => variant.id);
      return newVariants.filter(variant => !allVariantIdsOfBundle.includes(variant.id));
    };

    // 3 cases: add a new product group, add products to existing product group and update name of existing product group
    // Adding a new product group
    if (addProductsToProductGroupOpen && setProductGroupNameOpen && currentProductGroupIdOrKey === undefined) {
      const filteredNewVariants = getFilteredVariants(selectedNewVariants);
      const updatedVariants = [...variants, ...filteredNewVariants];
      const newItem = createAdminBundleItem(
        currentProductGroupName,
        filteredNewVariants.map(variant => variant.id),
        currentProductGroupNameTranslations,
      );
      const updatedItems = [...(items || []), newItem];
      const success = await handleVariantsChange(updatedVariants);
      if (success) {
        handleItemsChange(updatedItems);
      }
      setProductGroupsTableOpenStates([...productGroupsTableOpenStates, { itemIdOrKey: newItem.key, open: false }]);
    }
    // Adding products to existing product group
    else if (addProductsToProductGroupOpen && currentProductGroupIdOrKey !== undefined) {
      const filteredNewVariants = getFilteredVariants(selectedNewVariants);
      const updatedVariants = [...variants, ...filteredNewVariants];
      const itemToUpdateIndex = (items || []).findIndex(item => item.id === currentProductGroupIdOrKey || item.key === currentProductGroupIdOrKey);
      if (itemToUpdateIndex !== -1 && items) {
        const updatedOptionIds = [...items[itemToUpdateIndex].options, ...filteredNewVariants.map(variant => variant.id)];
        const updatedItem = { ...items[itemToUpdateIndex], options: updatedOptionIds };
        const updatedItems = updateEntityByIndex(items, itemToUpdateIndex, updatedItem);
        const success = await handleVariantsChange(updatedVariants);
        if (success) {
          handleItemsChange(updatedItems);
        }
      }
    }
    // Updating name of existing product group
    else if (setProductGroupNameOpen && currentProductGroupIdOrKey !== undefined) {
      const getTranslationId = (item: AdminBundleItemWithKey, localeId: number) => {
        return (item.translations || []).find(translation => translation.locale_id === localeId)?.id || 0;
      };
      const itemToUpdateIndex = (items || []).findIndex(item => item.id === currentProductGroupIdOrKey || item.key === currentProductGroupIdOrKey);
      if (itemToUpdateIndex !== -1 && items) {
        const updatedItemTranslations = currentProductGroupNameTranslations.map(t => {
          return { id: getTranslationId(items[itemToUpdateIndex], t.localeId), name: t.name, locale_id: t.localeId };
        });
        const updatedItem = { ...items[itemToUpdateIndex], name: currentProductGroupName, translations: updatedItemTranslations };
        const updatedItems = updateEntityByIndex(items, itemToUpdateIndex, updatedItem);
        handleItemsChange(updatedItems);
      }
    }
    handleCancelAddOrUpdateProductGroup();
  };

  const checkNewVariantSelected = (variant: VariantOverview) => {
    return selectedNewVariants.findIndex(selectedVariant => selectedVariant.id === variant.id) !== -1;
  };

  const handleSelectNewVariant = (variant: VariantOverview) => {
    setSelectedNewVariants([...selectedNewVariants, variant]);
  };

  const handleDeselectNewVariant = (variant: VariantOverview) => {
    const updatedSelectedNewVariants = selectedNewVariants.filter(selectedVariant => selectedVariant.id !== variant.id);
    setSelectedNewVariants(updatedSelectedNewVariants);
  };

  const handleProductChosen = async (productId: number, productName: string) => {
    try {
      const response = await productServiceAdminProductVariantsClient.getAdminProductVariants(productId);
      // Each product should have at least 1 variant - this is here just in case
      if (!response.data.variants || response.data.variants.length === 0) return;

      // Check if some variants are already part of bundle -> prevent bundle from having duplicate variants
      const variantsWithDuplicateInfo = response.data.variants.map(variant => {
        const allVariantIdsOfBundle = variants.map(variant => variant.id);
        return { ...variant, product_id: productId, duplicate: allVariantIdsOfBundle.includes(variant.id) };
      });

      // If product has only single variant and that is not already part of bundle -> choose that by default
      if (variantsWithDuplicateInfo.length === 1 && !variantsWithDuplicateInfo[0].duplicate) {
        setSelectedNewVariants(variantsWithDuplicateInfo);
      }
      setCurrentProductVariantOptions(variantsWithDuplicateInfo);
      setProductSearchInput(productName);
      setCurrentProductOptions([]);
      setShowingVariantOptions(true);
    } catch {
      setFetchProductError(true);
    }
  };

  const onProductSearchInputChange = async (searchInput: string) => {
    // TODO - improve: Request is made on every key press. Thus, if earlier is responded after latter then "ip" gives results for "i"
    // One solution could be canceling the previous request but this might not be possible if the auto generated client does not provide such functionality
    setProductSearchInput(searchInput);
    setShowingVariantOptions(false);
    setSelectedNewVariants([]);
    setFetchProductError(false);
    try {
      const response = await productServiceAdminProductsClient.getAdminProducts(undefined, undefined, undefined, searchInput, [searchCategory?.id]);
      // For filtering only valid variants of the same product. Compare first id (number to number) and if not found the secondary key (string to string)
      const firstOptionOfActiveItem = items?.find(
        item => currentProductGroupIdOrKey !== undefined && (item.id === currentProductGroupIdOrKey || item.key === currentProductGroupIdOrKey),
      )?.options?.[0];
      const optionVariantProductId = variants?.find(variant => variant.id === firstOptionOfActiveItem)?.product_id;
      const products = optionVariantProductId
        ? response.data.products.filter(product => product.id === optionVariantProductId)
        : response.data.products;

      setCurrentProductOptions(products);
      setSearchForProductsError(false);
    } catch {
      setSearchForProductsError(true);
    }
  };

  let errorText = '';
  if (searchForProductsError) {
    errorText = t('product_variants_section.addons.search_products_error');
  } else if (fetchProductError) {
    errorText = t('product_variants_section.addons.fetch_product_variants_error');
  }

  const searchFilteredNewProductVariantOptions = currentProductVariantOptions.filter(variant => {
    const nameToSearch = variant.name.replaceAll('/', '').replaceAll(' ', '').toLowerCase();
    const search = variantsSearchInput.replaceAll(' ', '').toLowerCase();
    return nameToSearch.includes(search);
  });

  const getSaveOrUpdateBtnState = () => {
    if (addProductsToProductGroupOpen && selectedNewVariants.length < 1) {
      return { disabled: true, title: t('product_variants_section.addons.add_variants_to_product_group_no_variants_title') };
    } else if (setProductGroupNameOpen && currentProductGroupName === '') {
      return { disabled: true, title: t('product_variants_section.addons.set_product_group_name_name_empty_title') };
    } else {
      return { disabled: false, title: '' };
    }
  };

  const getAddonValidationError = (item: AdminBundleItemWithKey) => {
    return (errors || []).find(error => error.itemIdOrKey === item.id || error.itemIdOrKey === item.key)?.error;
  };

  const summaryAutomaticPricingError = formulateBundleMainItemOrAddonsSummaryErrorMessage(t, erroneousSalesChannels);

  return (
    <Flex directionColumn>
      {/* Batch deletion of selected variants */}
      {selectedVariants.length > 0 && (
        <Div>
          <GridBag>
            <GridBag.Item md={6}>
              <BodyText fontWeight="bold">
                {t('product_variants_section.addons.selected_product_variants_count', { count: selectedVariants.length })}
              </BodyText>
            </GridBag.Item>
            <GridBag.Item md={6} alignTextToRight={true}>
              <Button variant="destructive" onClick={() => handleRemoveVariantsBtnClick()} size="md">
                {t('product_variants_section.addons.buttons.remove_product_variants')}
              </Button>
            </GridBag.Item>
          </GridBag>
          <Gutter size="sm" />
        </Div>
      )}

      <ModalDialog
        data-test-id="remove-addon-variants-confirmation"
        isOpen={removeVariantsConfirmationModalOpen}
        closeButtonText={t('product_variants_section.addons.remove_product_variants_confirmation.close')}
        acceptButtonText={t('product_variants_section.addons.remove_product_variants_confirmation.remove')}
        title={t('product_variants_section.addons.remove_product_variants_confirmation.title')}
        onCloseClick={() => setRemoveVariantsConfirmationModalOpen(false)}
        onAcceptClick={() => handleSelectedVariantsRemove()}
        destructive={true}
      >
        <BodyText>{t('product_variants_section.addons.remove_product_variants_confirmation.info')}</BodyText>
      </ModalDialog>

      {/* Nested table for all addons and the product variants that belong to them */}
      <GridBag>
        <GridBag.Item md={12}>
          <Table refElement={addonsTableRef} className="bundle_add_addons_product_variants__variants_table">
            <thead>
              <Table.Row>
                <Table.HeaderCell key="checkbox">
                  <Checkbox
                    checked={getAllVariantsOfAddons().length > 0 && checkAllVariantsSelected()}
                    onChange={() => (checkAllVariantsSelected() ? handleDeselectAllVariants() : handleSelectAllVariants())}
                  />
                </Table.HeaderCell>
                <Table.HeaderCell></Table.HeaderCell>
                <Table.HeaderCell key="product-variant-name">
                  <Div alignTextToCenter>
                    <BodyText fontWeight="bold">{t('product_variants_section.addons.product_variant_name_table_header')}</BodyText>
                  </Div>
                </Table.HeaderCell>
                <Table.HeaderCell key="product-variant-sku">
                  <Div alignTextToCenter>
                    <BodyText fontWeight="bold">{t('product_variants_section.addons.product_variant_sku_table_header')}</BodyText>
                  </Div>
                </Table.HeaderCell>
                <Table.HeaderCell key="product-variant-main"></Table.HeaderCell>
              </Table.Row>
            </thead>
            <tbody>
              {(items || []).map((item: AdminBundleItemWithKey) => (
                <React.Fragment key={item.id ? `id-${item.id}` : `key-${item.key}`}>
                  <Table.Row>
                    <Table.Cell>
                      <Checkbox
                        name={`select-item-${item.id}-all-variants`}
                        checked={checkVariantsOfItemSelected(item)}
                        onChange={() => (checkVariantsOfItemSelected(item) ? handleDeselectVariantsOfItem(item) : handleSelectVariantsOfItem(item))}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <ArrowButton
                        dataTestId={`open-item-${item.id}-variants-table-rows`}
                        open={checkProductGroupTableOpenState(item.id || item.key)}
                        onClick={() => toggleProductGroupTableOpenState(item.id || item.key)}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <BodyText>{item.name}</BodyText>
                    </Table.Cell>
                    <Table.Cell>
                      <BodyText alignTextToCenter>
                        {t('product_variants_section.addons.product_variants_in_group_count', { count: item.options.length })}
                      </BodyText>
                    </Table.Cell>
                    <Table.Cell>
                      {!addProductsToProductGroupOpen && !setProductGroupNameOpen && (
                        <BodyText
                          data-testid={`start-rename-item-${item.id}`}
                          clickableAppearance={true}
                          color="purple500"
                          onClick={() => handleStartUpdatingExistingProductGroupName(item)}
                        >
                          {t('product_variants_section.addons.rename_product_group')}
                        </BodyText>
                      )}
                    </Table.Cell>
                  </Table.Row>

                  {/* In case an addon has validation error, show it below its row in the table of addons */}
                  {getAddonValidationError(item) && (
                    <Table.Row>
                      <Table.Cell>
                        <BodyText size="sm" color="red600">
                          {getAddonValidationError(item)}
                        </BodyText>
                      </Table.Cell>
                    </Table.Row>
                  )}

                  {/* For each addon, check whether user has wanted to see what product variants belong to the addon */}
                  {checkProductGroupTableOpenState(item.id || item.key) && (
                    <>
                      {item.options
                        .map(option => variants.find(variant => variant.id === option))
                        .filter(isDefined)
                        .map(variant => {
                          const automaticPricingError = formulateSingleVariantErrorMessage(t, variant, false);
                          return (
                            <React.Fragment key={variant.id}>
                              <Table.Row>
                                <Table.Cell></Table.Cell>
                                <Table.Cell>
                                  <Checkbox
                                    name={`select-addon-item-variant-${variant.id}`}
                                    checked={checkVariantSelected(variant.id)}
                                    onChange={() =>
                                      checkVariantSelected(variant.id) ? handleDeselectVariant(variant.id) : handleSelectVariant(variant.id)
                                    }
                                  />
                                </Table.Cell>
                                <Table.Cell>
                                  <BodyText alignTextToCenter>{variant.name}</BodyText>
                                </Table.Cell>
                                <Table.Cell>
                                  <BodyText alignTextToCenter>{variant.sku}</BodyText>
                                </Table.Cell>
                                <Table.Cell>
                                  <Flex
                                    clickableAppearance={true}
                                    color="purple500"
                                    onClick={() => history.push(`/product/${variant.product_id}`)}
                                    centeredVertically
                                  >
                                    <BodyText>{t('product_variants_section.addons.view_product')}</BodyText>
                                    <Icon name="external" size="sm" margin={{ left: 'xs' }} />
                                  </Flex>
                                </Table.Cell>
                              </Table.Row>
                              {automaticPricingError && (
                                <Table.Row className="bundle_add_addons_product_variants__automatic_pricing_error_table_row">
                                  <Table.Cell />
                                  <Table.Cell />
                                  <Table.Cell colSpan={3}>
                                    <InlineNotification
                                      data-testid={`addon-item-${item.id}-automatic-pricing-variant-${variant.id}-error`}
                                      forceMultiline
                                      title={automaticPricingError.title}
                                      variant="warning"
                                    >
                                      {automaticPricingError.message}
                                    </InlineNotification>
                                  </Table.Cell>
                                </Table.Row>
                              )}
                            </React.Fragment>
                          );
                        })}
                      {!addProductsToProductGroupOpen && !setProductGroupNameOpen && (
                        <Table.Row>
                          <Table.Cell></Table.Cell>
                          <Table.Cell colSpan={4}>
                            <BodyText
                              clickableAppearance={true}
                              color="purple500"
                              onClick={() => handleStartUpdatingExistingProductGroupProducts(item)}
                            >
                              {t('product_variants_section.addons.add_product_variants_to_product_group')}
                            </BodyText>
                          </Table.Cell>
                        </Table.Row>
                      )}
                    </>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </Table>
        </GridBag.Item>
      </GridBag>

      {/* Open section for adding new product group to be part of addons */}
      {!setProductGroupNameOpen && !addProductsToProductGroupOpen && (
        <Div>
          <Gutter size="sm" />
          <BodyText margin={{ right: 'md' }} clickableAppearance={true} color="purple500" onClick={() => handleStartAddingProductGroup()}>
            {`+ ${t('product_variants_section.addons.add_product_group')}`}
          </BodyText>
        </Div>
      )}

      {summaryAutomaticPricingError && (
        <>
          <Gutter />
          <InlineNotification
            data-testid={`addon-items-automatic-pricing-summary-error`}
            forceMultiline
            title={summaryAutomaticPricingError.title}
            variant="warning"
          >
            {summaryAutomaticPricingError.message}
          </InlineNotification>
        </>
      )}

      {/* Add new product group to be part of addons or add products to existing product group or update name of existing product group */}
      {(setProductGroupNameOpen || addProductsToProductGroupOpen) && (
        <Div
          refElement={addOrUpdateAddonFormRef}
          backgroundColor="gray100"
          className="bundle_add_addons_product_variants__search_and_add_variants_section_div"
        >
          <Div padding={{ vertical: 'md', horizontal: 'sm' }}>
            <Flex directionColumn>
              <Title size="sm">
                {t(
                  setProductGroupNameOpen && addProductsToProductGroupOpen
                    ? 'product_variants_section.addons.add_product_group_title'
                    : 'product_variants_section.addons.update_product_group_title',
                )}
              </Title>
              {addProductsToProductGroupOpen && (
                <Div>
                  <Gutter />
                  <BodyText>{t('product_variants_section.addons.add_products_to_product_group_info')}</BodyText>
                </Div>
              )}
              <Gutter />

              {/* Adding a new product group or updating name of existing product group */}
              {setProductGroupNameOpen && (
                <Div>
                  <GridBag>
                    <GridBag.Item md={6}>
                      <EditableItem backgroundHoverColor="gray200" divProps={{ padding: { size: 'sm' } }}>
                        <EditableItem.Input
                          value={currentProductGroupNameTranslation?.name}
                          edit={true}
                          inputProps={{
                            name: 'product-group-name-translation',
                            onBlur: (e: React.FocusEvent<HTMLInputElement>) => handleChangeProductGroupNameTranslation(e.target.value),
                            placeholder: t('product_variants_section.addons.product_group_name_translation_placeholder', {
                              language: currentLocale.name.split(' ')[0],
                            }),
                            label: t('product_variants_section.addons.product_group_name_translation_label'),
                            onKeyDown: () => undefined,
                          }}
                        />
                        <Gutter size="sm" />
                        <EditableItem.Input
                          value={currentProductGroupName}
                          edit={productGroupNameReferenceInputOpen}
                          isReference={true}
                          focusOnEdit={true}
                          inputProps={{
                            name: 'product-group-name',
                            onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                              setCurrentProductGroupName(e.target.value);
                              setProductGroupNameReferenceInputOpen(false);
                            },
                            placeholder: t('product_variants_section.addons.product_group_name_reference_placeholder'),
                            label: t('product_variants_section.addons.product_group_name_reference_label'),
                            onKeyDown: () => undefined,
                            required: true,
                          }}
                          editIconProps={{
                            'data-testid': `product-group-name-edit`,
                            onClick: () => setProductGroupNameReferenceInputOpen(true),
                          }}
                        />
                      </EditableItem>
                    </GridBag.Item>
                  </GridBag>
                  <Gutter />
                </Div>
              )}

              {/* Add products to either new product group or to existing product group */}
              {addProductsToProductGroupOpen && (
                <Div>
                  <GridBag>
                    <GridBag.Item md={7}>
                      <SearchInputWithScope
                        showLabel={true}
                        label={t('product_variants_section.addons.search_select_product_label')}
                        placeholder={t('product_variants_section.addons.search_placeholder')}
                        errorText={errorText}
                        searchValue={productSearchInput}
                        onSearchChange={(event, value) => onProductSearchInputChange(value)}
                        searchOptions={currentProductOptions.map(product => ({ label: product.name, value: product.id.toString() }))}
                        onSearchOptionSelect={(event, option) => handleProductChosen(Number(option.value), option.label)}
                        scopeOptions={allCategoriesOfSystem.map(category => ({ label: category.name, value: category.id.toString() }))}
                        selectedScope={{ label: searchCategory?.name, value: searchCategory?.id.toString() }}
                        onScopeChange={(event, option) => handleSearchCategoryChange(Number(option.value))}
                      />
                    </GridBag.Item>
                  </GridBag>

                  {showingVariantOptions && (
                    <Div>
                      {currentProductVariantOptions.length > 1 && (
                        <Div>
                          <Gutter />
                          <Flex directionColumn>
                            <BodyText fontWeight="bold">{t('product_variants_section.addons.choose_variants_info')}</BodyText>
                          </Flex>
                          <Gutter size="xs" />
                          <GridBag>
                            <GridBag.Item md={6}>
                              <Input
                                name="addon-item-variants-search-input"
                                label=""
                                className="bundle_add_addons_product_variants__new_variants_search_input"
                                value={variantsSearchInput}
                                placeholder={t('product_variants_section.addons.variants_search_placeholder')}
                                onChange={(event, value) => setVariantsSearchInput(value)}
                              />
                            </GridBag.Item>
                          </GridBag>
                        </Div>
                      )}
                      <Gutter />
                      <Table className="bundle_add_addons_product_variants__variants_table">
                        <tbody>
                          {searchFilteredNewProductVariantOptions.map((variant: VariantOverview & { duplicate: boolean }) => (
                            <Table.Row
                              key={variant.id}
                              title={variant.duplicate ? t('product_variants_section.addons.duplicate_product_variant') : ''}
                            >
                              <Table.Cell>
                                <Checkbox
                                  name={`select-new-addon-item-variant-${variant.id}`}
                                  disabled={variant.duplicate}
                                  checked={checkNewVariantSelected(variant)}
                                  onChange={() =>
                                    checkNewVariantSelected(variant) ? handleDeselectNewVariant(variant) : handleSelectNewVariant(variant)
                                  }
                                />
                              </Table.Cell>
                              <Table.Cell>
                                <BodyText alignTextToCenter>{variant.name}</BodyText>
                              </Table.Cell>
                              <Table.Cell>
                                <BodyText alignTextToCenter>{variant.sku}</BodyText>
                              </Table.Cell>
                            </Table.Row>
                          ))}
                        </tbody>
                      </Table>
                    </Div>
                  )}
                  <Gutter />
                </Div>
              )}

              {/* Save changes to either create new product group or update existing product group */}
              <GridBag>
                <GridBag.Item md={5}>
                  <Flex>
                    <Button
                      id={saveProductGroupButtonId}
                      variant="primary"
                      disabled={getSaveOrUpdateBtnState().disabled}
                      title={getSaveOrUpdateBtnState().title}
                      onClick={async () => {
                        await ensureEditableItemInputChangesSavedOnSaveBtnClick(saveProductGroupButtonId);
                        setSaveProductGroupChanges(true);
                      }}
                    >
                      {t(
                        setProductGroupNameOpen && addProductsToProductGroupOpen
                          ? 'product_variants_section.addons.add_product_group_btn'
                          : 'product_variants_section.addons.update_product_group_btn',
                      )}
                    </Button>
                    <Gutter />
                    <Flex flexItem alignBaseline>
                      <Button
                        textVariantProps={{ color: 'black' }}
                        size={'xl'}
                        onClick={() => handleCancelAddOrUpdateProductGroup()}
                        variant={'text'}
                      >
                        {t('product_variants_section.addons.cancel_btn')}
                      </Button>
                    </Flex>
                  </Flex>
                </GridBag.Item>
              </GridBag>
            </Flex>
          </Div>
        </Div>
      )}
    </Flex>
  );
}

export default React.memo(Addons);
